import React, { useContext, useEffect, useState } from "react"
import propTypes from "prop-types"
import i18n from "i18n"
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
} from "@mui/material"
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded"
import PacoSendImage from "assets/images/budgeting/Download.png"
import { useParams } from "react-router-dom"
import { isNil } from "lodash"
import ProjectContext from "context/projects/projectContext"
import AuthContext from "context/auth/authContext"
import DownloadBlockModal from "./DownloadBlockModal"
import Budget2023Context from "context/budget2023/Budget2023Context"
import ModalCallError from "common/modalCallError"
import ModalOpenDownload from "common/ModalOpenDownload"
import * as amplitude from "@amplitude/analytics-browser"
import DownloadBudgetEnum from "common/DownloadBudgetEnum"
import CertHistoryContext from "context/certHistory/CertHistoryContext"

import ModalWaitProcess from "common/ModalWaitProcess"
import useDownloadBudgetConfig, {
  DOWNLOAD_TYPES,
} from "hooks/budgeting/useDownloadBudgetConfig"

import { useExcelOptions } from "hooks/downloadBudget/useExcelOptions"
import ExcelOptions from "./ExcelOptions"
import { usePdfOptions } from "hooks/downloadBudget/usePdfOptions"
import PdfOptions from "./PdfOptions"
import FilterToggleButtonGroup from "./FilterToggleButtonGroup"
import BC3Options from "./BC3Options"
import { useBC3Options } from "hooks/downloadBudget/useBC3Options"
import useEntitlementGuard from "hooks/useEntitlementGuard"
import { EXPORT_BC3, EXPORT_EXCEL, EXPORT_PDF } from "constants/entitlements"

const SendModal = {
  position: "absolute",
  top: "0%",
  right: "0%",
  height: "100%",
  maxWidth: "425px",
  backgroundColor: "white",
  paddingRight: "0px",
  borderRadius: "16px 0px 0px 16px",
  overflowY: "scroll",
  overflowX: "hidden",
}

const DownloadBudget = ({
  open,
  setOpen,
  name,
  soyFinal,
  fromTab = DownloadBudgetEnum.DEFAULT,
}) => {
  const { projectId } = useParams()
  const { projectInfo, getProject } = useContext(ProjectContext)
  const { exportSideBar } = useContext(CertHistoryContext)
  const {
    downloadBudget,
    urlDownload,
    pdfDownload,
    downloadCertification,
    bc3Download,
  } = useContext(Budget2023Context)

  const [downloadBudgetConfig, setDownloadBudgetConfig] =
    useDownloadBudgetConfig()

  const [checkEntitlement, entitlementDialog] = useEntitlementGuard()

  const excelData = useExcelOptions({
    downloadBudgetConfig,
  })
  const pdfData = usePdfOptions({
    downloadBudgetConfig,
  })
  const bc3Data = useBC3Options({
    downloadBudgetConfig,
  })

  const DESCRIPTION = true

  const typeBudget = soyFinal ? "PF" : "PC"
  const { user, getUser } = useContext(AuthContext)
  const [tipo, setTipo] = useState(DOWNLOAD_TYPES.EXCEL)

  const [openDownload, setOpenDownload] = useState(false)

  const [openModalWaitProcess, setOpenModalWaitProcess] = useState(false)

  useEffect(() => {
    if (!projectInfo) {
      getProject(projectId)
    }
    if (!user) {
      getUser()
    }
  }, [])

  const [openError, setOpenError] = useState(false)
  const [errorToSend, setErrorToSend] = useState("")
  const [openBlockModal, setOpenBlockModal] = useState(false)
  const [urlState, setUrlState] = useState("")

  const [processState, setProcessState] = useState({})
  //? PK="d102df6e-7c57-4155-acc1-6a8f35123c62#68c48ec4-e6a9-406c-bfee-21b5cb235142"
  //? SK="4b8fad12-2f64-4ceb-b81b-e0e8babe6c5b"

  const downloadExcelFromCertificationTab = async () => {
    let result = await downloadCertification(projectId, name)
    if (result?.error) {
      setOpenError(true)
      setErrorToSend(result)
    }
  }

  const downloadExcelFromHistoryCertificationTab = async () => {
    let result = await exportSideBar.downloadHistoryCertification(
      projectId,
      name,
      exportSideBar.selectedCertificationId,
    )
    if (result?.error) {
      setOpenError(true)
      setErrorToSend(result)
    }
  }

  const downloadExcelFromDefault = async () => {
    const options = {
      cost: excelData.costPrice,
      price: excelData.clientPrice,
      decomposition: excelData.decomposition,
      measures: excelData.measure,
      comment: excelData.comments,
    }

    let result = await downloadBudget(
      projectId,
      name,
      tipo,
      typeBudget,
      options,
    )
    if (result?.error) {
      setOpenError(true)
      setErrorToSend(result)
    }
  }

  const excelDownloadActions = {
    [DownloadBudgetEnum.DEFAULT]: { run: downloadExcelFromDefault },
    [DownloadBudgetEnum.FROM_CERTIFICATION]: {
      run: downloadExcelFromCertificationTab,
    },
    [DownloadBudgetEnum.FROM_HISTORY_CERTIFICATION]: {
      run: downloadExcelFromHistoryCertificationTab,
    },
  }

  const triggerExcelDownload = async () => {
    const actionSelected = excelDownloadActions[fromTab]
    actionSelected.run()
  }

  const getOptions = () => {
    return {
      envelope: pdfData.companyAndCustomerInfo,
      description: DESCRIPTION, //! check if this parameter is necessary
      cover: pdfData.addCover,
      briefing: pdfData.newFormat
        ? pdfData.briefingPosition
          ? { position: pdfData.briefingPosition }
          : {}
        : pdfData.briefingPosition,
      price: pdfData.clientPrice,
      mode: pdfData.newFormat ? "price" : "legacy",
      decomposition:
        tipo === DOWNLOAD_TYPES.EXCEL
          ? excelData.decomposition
          : tipo === DOWNLOAD_TYPES.PDF
            ? pdfData.decomposition
            : true,
      preview: false,
      conditions: pdfData.generalConditions,
      signature: pdfData.addSignature,
      header_date: pdfData.addDate,
      currency: "EUR",
      language: "es",
    }
  }

  const triggerPdfDownload = async () => {
    const options = getOptions()

    switch (options.mode) {
      case "legacy":
        const urlId = await pdfDownload(user, projectId, options)
        if (urlId?.id) {
          setUrlState(urlId.id)
          setOpenDownload(true)
        } else {
          setOpenError(true)
          setErrorToSend({
            error: true,
            message: "Error al descargar pdf",
            function: "/project/pdf/generate/" + projectId,
            service: "service-budget",
          })
        }
        break
      case "price":
        let newOption = options
        if (fromTab === DownloadBudgetEnum.FROM_CERTIFICATION) {
          newOption = {
            briefing: {},
            decomposition: false,
            mode: "certification",
            price: "client",
            certificationId: "",
          }
        }
        const result = await pdfDownload(user, projectId, newOption)

        if (result?.error) {
          setOpenError(true)
          setErrorToSend(result)
        } else {
          setProcessState(result)
          setOpenModalWaitProcess(true)
        }
        break
    }
  }

  const triggerBC3Download = async () => {
    const options = {
      addCosts: bc3Data.addCosts,
      addMeasurements: bc3Data.addMeasurements,
      addDecompositions: bc3Data.addDecompositions,
      simpleMode: bc3Data.simpleMode,
      name: name,
    }
    const result = await bc3Download(projectId, options)
    if (result?.error) {
      setOpenError(true)
      setErrorToSend(result)
    }
  }

  const downloadActions = {
    [DOWNLOAD_TYPES.EXCEL]: {
      event: "Budget Excel Downloaded",
      run: triggerExcelDownload,
    },
    [DOWNLOAD_TYPES.PDF]: {
      event: "Budget PDF Downloaded",
      run: triggerPdfDownload,
    },
    [DOWNLOAD_TYPES.BC3]: {
      event: "Budget BC3 Downloaded",
      run: triggerBC3Download,
    },
  }

  const handleClick = async e => {
    e.preventDefault()

    const actionSelected = downloadActions[tipo]

    if (!actionSelected) return

    if (
      tipo == DOWNLOAD_TYPES.PDF &&
      !checkEntitlement(EXPORT_PDF, "Exportar a PDF")
    )
      return

    if (
      tipo == DOWNLOAD_TYPES.BC3 &&
      !checkEntitlement(EXPORT_BC3, "Exportar a BC3")
    )
      return

    amplitude.track({
      event_type: actionSelected.event,
      event_properties: { projectId, projectName: name },
    })

    await actionSelected.run()
  }

  useEffect(() => {
    if (urlDownload == "done") {
      setOpen(false)
    }
  }, [urlDownload])

  const handleChangeTipo = (_event, newTipo) => {
    if (newTipo !== null) {
      setTipo(newTipo)
    }
  }

  const downloadOptionsComponents = {
    [DOWNLOAD_TYPES.EXCEL]: {
      Component: ExcelOptions,
      data: excelData,
    },
    [DOWNLOAD_TYPES.PDF]: {
      Component: PdfOptions,
      data: pdfData,
    },
    [DOWNLOAD_TYPES.BC3]: {
      Component: BC3Options,
      data: bc3Data,
    },
  }

  const SelectedDownloadComponent = downloadOptionsComponents[tipo]?.Component
  const selectedDownloadData = downloadOptionsComponents[tipo]?.data

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          null
        }}
      >
        <Box style={SendModal} component="form">
          <Grid
            container
            direction={"column"}
            marginTop={"0.5rem"}
            spacing={3}
            marginLeft={"-6px"}
            paddingRight={"10px"}
          >
            <Grid item marginLeft={"0rem"}>
              <IconButton
                sx={{
                  border: "1px solid #000001",
                  height: "25px",
                  width: "25px",
                  color: "black",
                  marginBottom: "1rem",
                }}
                onClick={() => {
                  setOpen(false)
                  exportSideBar.setSelectedCertificationId(null)
                }}
              >
                <ArrowForwardIosRoundedIcon style={{ width: "15px" }} />
              </IconButton>
            </Grid>
            <Box style={{ justifyContent: "center", display: "flex" }}>
              <img
                src={PacoSendImage}
                alt="PacoSend"
                width="100px"
                height="150px"
              />
            </Box>
            <Grid item>
              <p
                style={{
                  fontFamily: "Helvetica",
                  fontWeight: "600",
                  marginBottom: "0px",
                  fontSize: "16px",
                  color: "#414141",
                }}
              >
                {i18n.t("downloadBudget.downloadBudget")}
                {name}.
              </p>
            </Grid>

            <Grid item>
              <Typography
                variant="body1"
                align="left"
                style={{
                  fontFamily: "Helvetica",
                  fontWeight: "500",
                  fontSize: "14px",
                  color: "#414141",
                  marginBottom: "20px",
                }}
              >
                {i18n.t("downloadBudget.selectFormat")}
              </Typography>
            </Grid>
            <Box display={"flex"} justifyContent="left" ml="10px">
              <FilterToggleButtonGroup
                tipo={tipo}
                handleChangeTipo={handleChangeTipo}
                fromTab={fromTab}
              />
            </Box>

            {fromTab === DownloadBudgetEnum.DEFAULT &&
              SelectedDownloadComponent && (
                <>
                  <Grid item>
                    <Typography
                      variant="body1"
                      align="left"
                      style={{
                        fontFamily: "Helvetica",
                        fontWeight: "500",
                        fontSize: "14px",
                        color: "#414141",
                        marginBottom: "20px",
                      }}
                    >
                      {i18n.t("downloadBudget.selectFields")}
                    </Typography>
                  </Grid>

                  <Box display="column" justifyContent="left" ml="25px">
                    <SelectedDownloadComponent
                      setDownloadBudgetConfig={setDownloadBudgetConfig}
                      {...selectedDownloadData}
                    />
                  </Box>
                </>
              )}

            <Grid item marginTop={"0rem"}>
              <Typography
                variant="body1"
                align="left"
                style={{
                  fontFamily: "Helvetica",
                  fontWeight: "300",
                  fontSize: "13px",
                  marginTop: "20px",
                  paddingRight: "10PX",
                  color: "#414141",
                  marginBottom: "20px",
                }}
              >
                {i18n.t("downloadBudget.downloadLocation")}
              </Typography>
            </Grid>
            <Grid
              container
              alignItems={"center"}
              justifyContent={"center"}
              marginTop={"1rem"}
              marginBottom={"3rem"}
            >
              {urlDownload == "pending" || exportSideBar.isLoading ? (
                <CircularProgress size={24} style={{ marginLeft: "1rem" }} />
              ) : (
                <button
                  className="AddProject"
                  style={{
                    marginTop: "-10px",
                    position: "inherit",
                    marginLeft: "30px",
                  }}
                  onClick={handleClick}
                  data-amplify-analytics-on="click"
                  data-amplify-analytics-name="BudgetDownload"
                  data-amplify-analytics-attrs={projectInfo}
                >
                  {i18n.t("Descargar")}
                </button>
              )}
            </Grid>
          </Grid>
        </Box>
      </Modal>
      {openBlockModal && (
        <DownloadBlockModal
          open={openBlockModal}
          setOpen={setOpenBlockModal}
          name={name}
          tipo={tipo}
          typeBudget={typeBudget}
          muestraP={true}
          muestraUser={excelData.clientPrice}
          muestrCompany={pdfData.companyAndCustomerInfo}
          muestraDescription={DESCRIPTION}
          soyFinal={soyFinal}
        />
      )}
      {openError && (
        <ModalCallError
          open={openError}
          setOpen={setOpenError}
          error={errorToSend}
          ubicacion={"modal Download Presupuesto"}
        />
      )}
      {openDownload && urlState != "" && (
        <ModalOpenDownload
          setOpenError={setOpenError}
          open={openDownload}
          setOpen={setOpenDownload}
          projectId={projectId}
          urlId={urlState}
        />
      )}
      {openModalWaitProcess &&
        !isNil(processState) &&
        processState.PK &&
        processState.SK && (
          <ModalWaitProcess
            PK={processState.PK}
            SK={processState.SK}
            open={openModalWaitProcess}
            setOpen={setOpenModalWaitProcess}
          />
        )}
      {entitlementDialog}
    </>
  )
}
DownloadBudget.propTypes = {
  open: propTypes.bool.isRequired,
  setOpen: propTypes.func.isRequired,
  name: propTypes.string.isRequired,
  soyFinal: propTypes.bool,
  fromTab: propTypes.oneOf(Object.values(DownloadBudgetEnum)),
}
export default DownloadBudget
